// ABOUTME: Secure resume download component with permission validation and enhanced UX
// ABOUTME: Provides view/download options for authorized recruiters with proper file naming and access control

import React from 'react';
import { Button } from '@/components/ui/button';
import { FileText, Download, Eye, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';

interface SecureResumeDownloadProps {
  resumeUrl: string;
  candidateName: string;
  roleCreatedBy?: string;
  className?: string;
}

const SecureResumeDownload = ({ 
  resumeUrl, 
  candidateName, 
  roleCreatedBy, 
  className = "" 
}: SecureResumeDownloadProps) => {
  const { user } = useAuth();
  const { isAdmin } = useUserRoles();
  const { toast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);

  // Check if user has permission to access the resume
  const hasAccess = () => {
    if (!user) return false;
    if (isAdmin()) return true;
    if (roleCreatedBy && user.id === roleCreatedBy) return true;
    return false;
  };

  // Generate clean filename from candidate name
  const generateFileName = () => {
    const cleanName = candidateName
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .trim();
    return `${cleanName}_Resume.pdf`;
  };

  const handleView = () => {
    if (!hasAccess()) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to view this resume.",
        variant: "destructive",
      });
      return;
    }
    window.open(resumeUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = async () => {
    if (!hasAccess()) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to download this resume.",
        variant: "destructive",
      });
      return;
    }

    setIsDownloading(true);
    try {
      const response = await fetch(resumeUrl);
      if (!response.ok) throw new Error('Failed to fetch resume');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = generateFileName();
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      toast({
        title: "Download Complete",
        description: `${candidateName}'s resume has been downloaded.`,
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download the resume. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  if (!resumeUrl) {
    return (
      <div className={`flex items-center space-x-2 text-gray-400 ${className}`}>
        <FileText className="w-3 h-3" />
        <span className="text-sm">No resume</span>
      </div>
    );
  }

  if (!hasAccess()) {
    return (
      <div className={`flex items-center space-x-2 text-gray-400 ${className}`}>
        <FileText className="w-3 h-3" />
        <span className="text-sm">Resume (No access)</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center space-x-1 ${className}`}>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleView}
        className="h-auto p-1 text-blue-600 hover:text-blue-800"
        title="View resume in new tab"
      >
        <Eye className="w-3 h-3" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleDownload}
        disabled={isDownloading}
        className="h-auto p-1 text-green-600 hover:text-green-800"
        title={`Download ${generateFileName()}`}
      >
        {isDownloading ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <Download className="w-3 h-3" />
        )}
      </Button>
      <div className="flex items-center space-x-1 text-sm text-gray-600">
        <FileText className="w-3 h-3" />
        <span className="hover:underline cursor-pointer" onClick={handleView}>
          Resume
        </span>
      </div>
    </div>
  );
};

export default SecureResumeDownload;